import { ZONE_ICON_MAPPING } from '~/components/Map/constants'
import { MODULES_MAP_all_zones_nodes as ZONE } from '~/components/MapView/__generated__/MODULES_MAP'
import { ModuleHardware } from '~/gql/generated/graphql'
import zoneIconMap from '~/images/bays.png'

/**
 * Each zone is drawn as a single icon: the bay sprite from bays.png with its
 * vehicle counts painted on. Drawing the icon and counts as one image means
 * they can't come apart when bays overlap. Within a single IconLayer, later
 * items simply draw on top of earlier ones.
 *
 * The images are rendered on demand. getZoneIcon returns an icon descriptor
 * whose `url` describes the image to draw (see toUrl), and the IconLayer loads
 * it through zoneIconLoadOptions.fetch. deck.gl packs each distinct image into
 * its atlas once, keyed by id, so bays that look the same share an image.
 * Rendered images are also cached for the session, so opening another map
 * doesn't draw them again.
 */

// On-screen height of a bay icon, in pixels
export const ZONE_ICON_SIZE = 33

// Icons are rendered at this many image pixels per screen pixel: sharp on
// retina screens, without the memory cost of bays.png's full resolution. Each
// distinct icon stays in deck.gl's atlas for the life of the layer.
export const ZONE_ICON_RENDER_SCALE = 2

const URL_PREFIX = 'zone-icon:'

const FONT_FAMILY = 'Hellix-Bold, Helvetica, Arial, sans-serif'
const FONT_SIZE = 15
const TEXT_COLOUR = 'rgb(60, 60, 67)'

// Screen pixel offsets from the icon's centre to the centre of each count,
// by how many vehicle types the bay shows. An empty bay shows a single 0.
const COUNT_OFFSETS: Record<number, [number, number][]> = {
  0: [[-10, -2]],
  1: [[-10, -2]],
  2: [
    [-20 / 3, -2],
    [64 / 3, -2],
  ],
  3: [
    [-76 / 3, -2],
    [8 / 3, -2],
    [40, -2],
  ],
}

type ZoneIconSpec = {
  // Key into ZONE_ICON_MAPPING
  sprite: string
  // Counts to paint on, in slot order
  counts: string[]
}

export const getZoneVehicles = (z: ZONE) =>
  [
    { glyph: ModuleHardware.beryl_bike, count: z.bikes },
    { glyph: ModuleHardware.bbe, count: z.ebikes },
    { glyph: ModuleHardware.scooter, count: z.scooters },
  ].filter(({ count }) => count > 0)

export const getZoneIconSpec = (z: ZONE): ZoneIconSpec => {
  const vehicles = getZoneVehicles(z)
  const suspended = z.is_suspended === true ? 'suspended' : ''
  const sprite =
    (vehicles.length === 0
      ? 'empty'
      : vehicles.map(({ glyph }) => glyph).join('')) + suspended
  // Suspended bays don't show counts
  const counts = suspended
    ? []
    : vehicles.length === 0
      ? ['0']
      : vehicles.map(({ count }) => String(count))
  return { sprite, counts }
}

const toUrl = ({ sprite, counts }: ZoneIconSpec) =>
  `${URL_PREFIX}${sprite}/${counts.join(',')}`

const fromUrl = (url: string): ZoneIconSpec => {
  const [sprite, counts] = url.slice(URL_PREFIX.length).split('/')
  return { sprite, counts: counts ? counts.split(',') : [] }
}

// Size of the rendered image, in image pixels
const getRenderedSize = (sprite: string) => {
  const { width, height } = ZONE_ICON_MAPPING[sprite]
  const renderedHeight = ZONE_ICON_SIZE * ZONE_ICON_RENDER_SCALE
  return {
    width: Math.round((width * renderedHeight) / height),
    height: renderedHeight,
  }
}

export const getZoneIcon = (z: ZONE) => {
  const spec = getZoneIconSpec(z)
  const { width, height } = getRenderedSize(spec.sprite)
  const url = toUrl(spec)
  return { id: url, url, width, height }
}

let atlas: Promise<HTMLImageElement> | undefined
const loadAtlas = () => {
  atlas ??= new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = zoneIconMap
  })
  return atlas
}

// Canvas text doesn't wait for web fonts, so make sure Hellix is ready first.
// If it fails to load, draw with the fallback fonts rather than not at all.
const loadFont = () =>
  document.fonts.load(`${FONT_SIZE}px ${FONT_FAMILY}`).catch(() => undefined)

const renderZoneIcon = async (spec: ZoneIconSpec) => {
  const [image] = await Promise.all([loadAtlas(), loadFont()])
  const sprite = ZONE_ICON_MAPPING[spec.sprite]
  const { width, height } = getRenderedSize(spec.sprite)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not create a canvas to draw the zone icon')
  }
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    image,
    sprite.x,
    sprite.y,
    sprite.width,
    sprite.height,
    0,
    0,
    width,
    height,
  )

  // Scale screen pixel sizes up to image pixels
  const scale = ZONE_ICON_RENDER_SCALE
  ctx.font = `${FONT_SIZE * scale}px ${FONT_FAMILY}`
  ctx.fillStyle = TEXT_COLOUR
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const offsets = COUNT_OFFSETS[spec.counts.length]
  spec.counts.forEach((count, slot) => {
    const [dx, dy] = offsets[slot]
    ctx.fillText(count, width / 2 + dx * scale, height / 2 + dy * scale)
  })

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(blob =>
      blob ? resolve(blob) : reject(new Error('Could not encode zone icon')),
    ),
  )
}

// Rendered icons, by url. deck.gl's atlas belongs to one map and is thrown
// away when the map unmounts, so keep the encoded images for the rest of the
// session: each distinct icon is drawn once, however many maps show it. They
// are small PNGs, a few KB each.
const renderedIcons = new Map<string, Promise<Blob>>()

const getRenderedIcon = (url: string) => {
  let icon = renderedIcons.get(url)
  if (!icon) {
    icon = renderZoneIcon(fromUrl(url))
    // Don't keep failures, so the next map tries again
    icon.catch(() => renderedIcons.delete(url))
    renderedIcons.set(url, icon)
  }
  return icon
}

// IconLayer `loadOptions`. loaders.gl calls `fetch` with each icon's url and
// parses the response as an image.
export const zoneIconLoadOptions = {
  fetch: async (url: string) =>
    new Response(await getRenderedIcon(url), {
      headers: { 'Content-Type': 'image/png' },
    }),
}
